import { useLifetimeOffer } from '@/hooks/use-lifetime-offer';
import {
  createLifetimeCheckoutUrl,
  getLifetimeCheckoutErrorMessage,
  useResetLoadingOnBackNavigation
} from '@/services/stripe/lifetime-checkout.service';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useEffect, useState } from 'react';
import CLButton from '../ui-controls/default-ui-controls/button';
import { LoadingSpinBlackCustom } from '../utils/loading.component';
import LifetimeCountdownComponent from './lifetime-countdown.component';

/** Shown at most once per organization; the top bar carries the offer after. */
function getDismissedKey(orgId: string) {
  return `${orgId}-lifetimeOfferDismissed`;
}

/**
 * The one interruption the offer is allowed. It waits out the quiet period
 * after signup so a brand new user gets to look around first, then appears
 * once and never again for that organization.
 *
 * Everything is centered and stacked in reading order: what this is, what it
 * costs, why now, then the actions. The "Launch offer" label is a small
 * orange flag rather than a gold chip so it no longer reads as one element
 * with the timer below it.
 */
export default function LifetimeOfferPopupComponent() {
  const { isPopupDue, countdown, plan, planType, orgId } = useLifetimeOffer();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useResetLoadingOnBackNavigation(setIsLoading);

  useEffect(() => {
    if (!isPopupDue || !orgId) {
      return;
    }

    // localStorage is unavailable in some privacy modes. Failing to read it
    // should not take the popup down with it, so treat it as "not dismissed".
    let hasDismissed = false;
    try {
      hasDismissed = localStorage.getItem(getDismissedKey(orgId)) === 'true';
    } catch (error) {
      console.error(error);
    }

    if (!hasDismissed) {
      setIsOpen(true);
    }
  }, [isPopupDue, orgId]);

  function dismiss() {
    setIsOpen(false);

    if (!orgId) {
      return;
    }

    try {
      localStorage.setItem(getDismissedKey(orgId), 'true');
    } catch (error) {
      console.error(error);
    }
  }

  async function claim() {
    if (!orgId || !plan?.id) {
      return;
    }

    setErrorMessage('');
    setIsLoading(true);

    try {
      // A full-page navigation, not a new tab, so Stripe's redirect lands back
      // in this same session. Loading stays on until the browser leaves.
      window.location.href = await createLifetimeCheckoutUrl(orgId, plan.id);
    } catch (error) {
      setErrorMessage(getLifetimeCheckoutErrorMessage(error));
      setIsLoading(false);
    }
  }

  if (!isOpen) {
    return null;
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={dismiss}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-3xl bg-white align-middle shadow-xl transition-all">
                <div className="relative p-6 text-center sm:p-8">
                  <CLButton
                    className="absolute right-5 top-5 p-1"
                    onClick={dismiss}
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0.292893 0.292893C0.683417 -0.0976311 1.31658 -0.0976311 1.70711 0.292893L8 6.58579L14.2929 0.292893C14.6834 -0.0976311 15.3166 -0.0976311 15.7071 0.292893C16.0976 0.683417 16.0976 1.31658 15.7071 1.70711L9.41421 8L15.7071 14.2929C16.0976 14.6834 16.0976 15.3166 15.7071 15.7071C15.3166 16.0976 14.6834 16.0976 14.2929 15.7071L8 9.41421L1.70711 15.7071C1.31658 16.0976 0.683417 16.0976 0.292893 15.7071C-0.0976311 15.3166 -0.0976311 14.6834 0.292893 14.2929L6.58579 8L0.292893 1.70711C-0.0976311 1.31658 -0.0976311 0.683417 0.292893 0.292893Z"
                        fill="black"
                        fillOpacity="0.5"
                      />
                    </svg>
                  </CLButton>

                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#FAF0D3] sm:h-16 sm:w-16">
                    <svg
                      width="26"
                      height="26"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M13 2L5 13H11L10 22L18 10H12L13 2Z"
                        stroke="black"
                        strokeWidth="1.8"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  <div className="mt-5 text-lg font-bold text-[#E08A6A]">
                    Launch offer
                  </div>

                  <Dialog.Title
                    as="h2"
                    className="mt-1 text-2xl font-bold sm:text-3xl"
                  >
                    {planType?.name ?? 'Lifetime plan'}
                  </Dialog.Title>

                  <div className="mt-4 flex flex-wrap items-baseline justify-center gap-2 sm:gap-3">
                    <div className="text-4xl font-extrabold sm:text-5xl">
                      ${plan?.pricing_price?.amount}
                    </div>
                    <div className="text-base opacity-60 sm:text-lg">
                      one-time payment
                    </div>
                  </div>

                  <p className="mt-4 text-sm opacity-60 sm:text-base">
                    Pay once and keep it — no renewals, no monthly bill. Open
                    for your first 24 hours only.
                  </p>

                  <div className="mt-6">
                    <LifetimeCountdownComponent
                      countdown={countdown}
                      size="lg"
                    />
                    <span className="sr-only">
                      {countdown.hours} hours and {countdown.minutes} minutes
                      left to claim
                    </span>
                  </div>

                  {errorMessage && (
                    <div role="alert" className="mt-4 text-sm text-red-600">
                      {errorMessage}
                    </div>
                  )}

                  <div className="mt-7 flex flex-col gap-2">
                    <CLButton
                      className="flex w-full items-center justify-center gap-2 rounded-full bg-[#E8A94F] py-3 text-base font-bold text-black transition hover:brightness-105 disabled:opacity-60 sm:py-3.5 sm:text-lg"
                      disabled={isLoading}
                      onClick={claim}
                    >
                      {isLoading && (
                        <LoadingSpinBlackCustom className="w-4 h-4"></LoadingSpinBlackCustom>
                      )}
                      Claim lifetime deal
                    </CLButton>

                    <CLButton
                      className="w-full py-2 text-base opacity-60"
                      onClick={dismiss}
                    >
                      Maybe later
                    </CLButton>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
